import axios from 'axios';
import { store } from '../store/store';
import { logout, refreshSessionSuccess } from '../store/authSlice';
import { API_ENDPOINTS } from '../config/constants';
import { normalizeAuthResponse } from './authResponse';

// REACT_APP_API_BASE_URL is inlined at build time by CRA. The
// prebuild-check.js script enforces the production value is either
// unset, "/api", or "https://..." (never http://localhost or
// http://127.0.0.1) — so the production bundle is always either
// same-origin (no CORS, no mixed-content) or a deliberate cross-host
// https:// origin. The banner below is a runtime sanity check; if
// the value is "http://localhost..." or "http://127.0.0.1..." we
// know a dev build leaked into prod, and we want the operator to
// see the warning in the browser console before users hit it.
//
// Defensive measure 3 of the 4-measure set added 2026-10-16 after
// a stale "http://localhost:8000" baked into the prod bundle caused
// "An error occurred during login" for every user (net::ERR_CONNECTION_REFUSED
// on http://localhost:8000/access/login from the user's browser).
const RAW_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();
const RESOLVED_BASE_URL =
  RAW_BASE_URL === ''
    ? '(same-origin — REACT_APP_API_BASE_URL not set)'
    : RAW_BASE_URL;

if (typeof window !== 'undefined' && typeof window.console !== 'undefined') {
  // %c styled banner — easy to spot in devtools.
  // eslint-disable-next-line no-console
  window.console.log(
    '%c[LGS FE] axios baseURL = %c%s',
    'color:#6b46c1;font-weight:bold',
    'color:#1a202c;font-weight:bold',
    RESOLVED_BASE_URL
  );
  if (
    RAW_BASE_URL.startsWith('http://localhost') ||
    RAW_BASE_URL.startsWith('http://127.0.0.1')
  ) {
    // eslint-disable-next-line no-console
    window.console.warn(
      '%c[LGS FE] WARNING: axios baseURL points at a loopback origin (%s). ' +
        'This is a dev-only value — prebuild-check.js should have refused this ' +
        'build. If you are seeing this in production, the prod env was not ' +
        'set correctly. /api reverse-proxy will still be bypassed because the ' +
        'baseURL is absolute; every API call will hit the user\'s own machine.',
      'color:#c53030;font-weight:bold',
      RAW_BASE_URL
    );
  }
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: true,
  timeout: 180000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const authApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: true,
  timeout: 180000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshTokenRequest = null;
let hasRedirectedAfterAuthFailure = false;

const isRefreshRequest = (config = {}) => {
  const requestUrl = String(config.url || '');

  return requestUrl.includes(API_ENDPOINTS.REFRESH_TOKEN);
};

const redirectToLogin = () => {
  store.dispatch(logout());

  if (hasRedirectedAfterAuthFailure) return;
  hasRedirectedAfterAuthFailure = true;

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const refreshAccessToken = async () => {
  const refreshToken = store.getState().auth.refreshToken;

  if (!refreshToken) {
    throw new Error('Refresh token is not available.');
  }

  const response = await authApi.post(API_ENDPOINTS.REFRESH_TOKEN, {
    refresh_token: refreshToken,
  });
  const authPayload = normalizeAuthResponse(response.data);

  if (!authPayload?.token) {
    throw new Error('Refresh response did not include an access token.');
  }

  store.dispatch(refreshSessionSuccess(authPayload));
  hasRedirectedAfterAuthFailure = false;

  return authPayload.token;
};

api.interceptors.request.use(
  (config) => {
    const token = store.getState().auth.token;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config || {};
    const token = store.getState().auth.token;

    if (
      error.response?.status !== 401 ||
      !token ||
      originalRequest._retry ||
      isRefreshRequest(originalRequest)
    ) {
      return Promise.reject(error);
    }

    const refreshToken = store.getState().auth.refreshToken;

    if (!refreshToken) {
      redirectToLogin();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!refreshTokenRequest) {
      refreshTokenRequest = refreshAccessToken()
        .catch((refreshError) => {
          redirectToLogin();
          throw refreshError;
        })
        .finally(() => {
          refreshTokenRequest = null;
        });
    }

    return refreshTokenRequest.then((newToken) => {
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      return api(originalRequest);
    });
  }
);

export default api;
